import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2ReportAnswer,
    IChecklist2ReportAnswerDetailsCheckpointGroup,
    IChecklist2ReportAnswerDetailsPeriod,
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportHistoryUnits,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
    ICheckpoint2GroupForReport,
    ICheckpointV2ForReport,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiHistoryChecklistDetailsAnswersDownloadDialogComponent } from '@fi-shared/checklist/fi-checklist2-report/components/checklist-history-report/checklist-details/answers-download-dialog/answers-download-dialog.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { takeUntil } from 'rxjs/operators';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FileType } from '@fi-shared/types';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

interface IGetSelectedItemsDetailedReportHeadingArgs {
    items: { Id: number; Name: string }[];
    selectedItemIds: number[];
    headingLabelSingle: string;
    headingLabelPlural: string;
    unitName: string;
    checklistName: string;
    scheduleName: string;
}

@Component({
    selector: 'fi-history-checklist-detailed-answers',
    templateUrl: './detailed-answers.component.html',
    styleUrls: ['./detailed-answers.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-history-checklist-detailed-answers' },
    standalone: false,
})
export class FiHistoryChecklistDetailedAnswersComponent implements OnInit, OnDestroy, OnChanges {
    @Input({ required: true }) reportParameter: IChecklistV2ReportParameter;
    @Input({ required: true }) reportData: FiChecklist2ReportData;
    @Input({ required: true }) checklistUnits: IChecklist2ReportHistoryUnits[];
    @Input({ required: true }) detailedAnswerFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) checklistName: string;
    @Input({ required: true }) scheduleName: string;
    @Input() filterChanged$: Observable<void>;

    @Input() scrollableEl: HTMLElement;

    protected currentPage: number = 1;
    protected totalPages: number;
    protected currentPeriod: IChecklist2ReportAnswerDetailsPeriod;
    protected totalComplianceResult: number;
    protected checkpointGroups: IChecklist2ReportAnswerDetailsCheckpointGroup[];
    protected answerDetails$ = new BehaviorSubject<IChecklist2ReportAnswer[]>([]);
    

    private fiChecklist2ReportHistoryService = inject(FiChecklist2ReportHistoryService);
    private fiChecklist2ReportDataService = inject(FiChecklist2ReportDataService);
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);
    private cdr = inject(ChangeDetectorRef);
    private fiScrollToElementAnimateService = inject(FiScrollToElementAnimateService);
    private elementRef = inject(ElementRef);
    private fiPrintDownloadService = inject(FiPrintDownloadService);

    protected isDownloadingDisabled$ = this.fiPrintDownloadService.isDownloadingInProcess$;
    
    public ngOnInit(): void {
        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => this.loadDetailedAnswersData(),
        );
        this.filterChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.loadDetailedAnswersData());
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.detailedAnswerFilter;
        if (change && change.currentValue) {
            this.currentPage = 1;
            this.loadDetailedAnswersData();
            this.scrollToView();
        }
    }

    protected isSectionSummaryVisible(): boolean {
        return (
            !this.detailedAnswerFilter.CheckpointIds?.length && !this.detailedAnswerFilter.CheckpointStatusTypes?.length
        );
    }

    protected isTotalSummaryVisible(): boolean {
        return (
            !this.detailedAnswerFilter.CheckpointIds?.length &&
            !this.detailedAnswerFilter.CheckpointStatusTypes?.length &&
            this.isAllAvailableCheckpointGroupsSelected()
        );
    }

    private isAllAvailableCheckpointGroupsSelected(): boolean {
        if (!this.detailedAnswerFilter.CheckpointGroupIds?.length) return true;

        const checklistGroups: ICheckpoint2GroupForReport[] = this.reportData.availableItems.CheckpointGroups.filter(
            (checkpointGroup: ICheckpoint2GroupForReport) =>
                checkpointGroup.ChecklistId === this.detailedAnswerFilter.ChecklistIds[0],
        );

        return this.detailedAnswerFilter.CheckpointGroupIds.length === checklistGroups.length;
    }

    private getSelectedItemsDetailedReportHeading(args: IGetSelectedItemsDetailedReportHeadingArgs): string {
        const itemNames: string[] = args.items
            .filter((item) => args.selectedItemIds.includes(item.Id))
            .map((item) => item.Name);

        if (itemNames.length === 1)
            return this.appTextService.get(args.headingLabelSingle, {
                '{0}': args.unitName,
                '{1}': args.checklistName,
                '{2}': args.scheduleName,
                '{3}': itemNames[0],
            });

        return this.appTextService.get(args.headingLabelPlural, {
            '{0}': args.unitName,
            '{1}': args.checklistName,
            '{2}': args.scheduleName,
        });
    }

    protected getDetailedReportHeading(): string {
        const businessUnitId: number = this.detailedAnswerFilter.BusinessUnitIds[0];
        const historyChecklistUnit: IChecklist2ReportHistoryUnits = this.checklistUnits.find(
            (checklistUnit: IChecklist2ReportHistoryUnits) => {
                return checklistUnit.BusinessUnitId === businessUnitId;
            },
        );
        const unitName: string = historyChecklistUnit.BusinessUnitName;
        const checklistName: string = this.checklistName;
        const scheduleName: string = this.scheduleName;

        if (this.detailedAnswerFilter.CheckpointGroupIds?.length) {
            return this.getSelectedItemsDetailedReportHeading({
                items: this.reportData.availableItems.CheckpointGroups,
                selectedItemIds: this.detailedAnswerFilter.CheckpointGroupIds,
                headingLabelSingle: 'checklist.report_detailed_unit_in_checklist_checkpoint_group',
                headingLabelPlural: 'checklist.report_detailed_unit_in_checklist_checkpoint_group_plural',
                unitName,
                checklistName,
                scheduleName,
            });
        }

        if (this.detailedAnswerFilter.CheckpointIds?.length) {
            return this.getSelectedItemsDetailedReportHeading({
                items: this.reportData.availableItems.Checkpoints,
                selectedItemIds: this.detailedAnswerFilter.CheckpointIds,
                headingLabelSingle: 'checklist.report_detailed_unit_in_checklist_checkpoint',
                headingLabelPlural: 'checklist.report_detailed_unit_in_checklist_checkpoint_plural',
                unitName,
                checklistName,
                scheduleName,
            });
        }

        const checklistDetailedReportLabel: string = this.detailedAnswerFilter.CheckpointStatusTypes?.includes(
            Checkpoint2StatusType.NotOk,
        )
            ? 'checklist.report_detailed_unit_in_checklist_deviations'
            : 'checklist.report_detailed_unit_in_checklist';
        return this.appTextService.get(checklistDetailedReportLabel, {
            '{0}': unitName,
            '{1}': checklistName,
            '{2}': scheduleName,
        });
    }

    protected onCurrentPageChanged($event: number): void {
        this.loadDetailedAnswersData();
    }

    private _isFirstLoad: boolean = true;

    protected loadDetailedAnswersData(): void {
        const checklistHistoryDateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
        this.fiChecklist2ReportHistoryService
            .getDetailedAnswers(
                this.detailedAnswerFilter,
                checklistHistoryDateRange,
                this.reportData.auditMode,
                this.currentPage,
            )
            .then((result) => {
                this.totalPages = result.TotalPages;

                this.currentPeriod = result.ItemsData.Period;
                this.totalComplianceResult = result.ItemsData.ComplianceResult;
                this.checkpointGroups = result.ItemsData.CheckpointGroups;

                this.answerDetails$.next(result.ItemsData.Answers);
                this.cdr.markForCheck();
                if (this._isFirstLoad) {
                    this.scrollToView();
                }
                this._isFirstLoad = false;
            });
    }

    private scrollToView(): void {
        this.fiScrollToElementAnimateService.scrollToElementIntoView(this.elementRef.nativeElement, this.scrollableEl);
    }

    protected isDataLoaded(): boolean {
        return isDefined(this.totalPages);
    }

    protected openDownloadReportDialog(): void {
        this.fiDialogsService
            .custom({
                contentComponentRef: FiHistoryChecklistDetailsAnswersDownloadDialogComponent,
                contentComponentData: {
                    pagesCount: this.totalPages,
                },
                isTopCloseButtonShown: true,
                headerText: this.appTextService.get('checklist.history_report_download'),
                headerIconClass: 'BrandedColor far fa-download',
                yesButtonText: this.appTextService.get('common.download'),
                noButtonText: this.appTextService.get('common.cancel'),
                preventUppercaseButtonLabel: true,
                config: {
                    autoFocus: false,
                    restoreFocus: false,
                    panelClass: 'fi-rounded-dialog-panel',
                },
            })
            .onYesButtonClick((result) => {
                this.downloadAnswerDetailsReport(
                    result.reportFormat,
                    result.isFullReport ? undefined : this.currentPage,
                );
            });
    }

    protected downloadAnswerDetailsReport(reportFormat: FileType, pageToDownload?: number): void {
        const checklistHistoryDateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
        const detailedAnswersFilter = this.detailedAnswerFilter;
        this.fiChecklist2ReportHistoryService.downloadAnswerDetailsReport({
            currentFilter: detailedAnswersFilter,
            checklistHistoryDateRange: checklistHistoryDateRange,
            reportType: this.reportParameter.ReportType,
            auditMode: this.reportData.auditMode,
            reportFormat: reportFormat,
            pageToDownload: pageToDownload,
        });
    }

    private ngOnDestroy$ = new Subject<void>();

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
